import { lazy, Suspense, type ComponentType } from "react";
import type { EmbedProps } from "./types";

type EmbedModule = { default: ComponentType<EmbedProps> };

const EMBED_LOADERS: Record<string, () => Promise<EmbedModule>> = {
  case_capture: () =>
    import("./CaseCaptureEmbed").then((m) => ({ default: m.CaseCaptureEmbed })),
  progeny_doc_pull: () =>
    import("./ProgenyDocPullEmbed").then((m) => ({ default: m.ProgenyDocPullEmbed })),
  case_triage: () =>
    import("./CaseTriageEmbed").then((m) => ({ default: m.CaseTriageEmbed })),
  case_check_status: () =>
    import("./CaseCheckStatusEmbed").then((m) => ({
      default: m.CaseCheckStatusEmbed,
    })),
  case_review: () =>
    import("./CaseReviewEmbed").then((m) => ({ default: m.CaseReviewEmbed })),
  disclosure_capture: () =>
    import("./DisclosureCaptureEmbed").then((m) => ({
      default: m.DisclosureCaptureEmbed,
    })),
  disclosure_triage: () =>
    import("./DisclosureTriageEmbed").then((m) => ({
      default: m.DisclosureTriageEmbed,
    })),
  disclosure_threshold: () =>
    import("./DisclosureThresholdEmbed").then((m) => ({
      default: m.DisclosureThresholdEmbed,
    })),
  disclosure_preform: () =>
    import("./DisclosurePreformEmbed").then((m) => ({
      default: m.DisclosurePreformEmbed,
    })),
  disclosure_review: () =>
    import("./DisclosureReviewEmbed").then((m) => ({
      default: m.DisclosureReviewEmbed,
    })),
  disclosure_audit: () =>
    import("./DisclosureAuditEmbed").then((m) => ({
      default: m.DisclosureAuditEmbed,
    })),
  meeting_capture: () =>
    import("./MeetingCaptureEmbed").then((m) => ({
      default: m.MeetingCaptureEmbed,
    })),
  meeting_transcript: () =>
    import("./MeetingTranscriptEmbed").then((m) => ({
      default: m.MeetingTranscriptEmbed,
    })),
  meeting_extractions: () =>
    import("./MeetingExtractionsEmbed").then((m) => ({
      default: m.MeetingExtractionsEmbed,
    })),
  meeting_observations: () =>
    import("./MeetingObservationsEmbed").then((m) => ({
      default: m.MeetingObservationsEmbed,
    })),
  meeting_crm_updates: () =>
    import("./MeetingCrmUpdatesEmbed").then((m) => ({
      default: m.MeetingCrmUpdatesEmbed,
    })),
  document_review: () =>
    import("./DocumentReviewEmbed").then((m) => ({
      default: m.DocumentReviewEmbed,
    })),
  mission_capture: () =>
    import("./MissionCaptureEmbed").then((m) => ({
      default: m.MissionCaptureEmbed,
    })),
  info_request: () =>
    import("./InfoRequestEmbed").then((m) => ({ default: m.InfoRequestEmbed })),
  portfolio: () =>
    import("./PortfolioEmbed").then((m) => ({ default: m.PortfolioEmbed })),
  portfolio_assessment: () =>
    import("./PortfolioAssessmentEmbed").then((m) => ({
      default: m.PortfolioAssessmentEmbed,
    })),
};

// One lazy() component per embed kind — avoids loading every embed chunk up front.
const lazyCache = new Map<string, ComponentType<EmbedProps>>();

/** Return a memoised ``lazy()`` component for one embed kind, or null when unregistered. */
function getLazyEmbed(kind: string): ComponentType<EmbedProps> | null {
  const loader = EMBED_LOADERS[kind];
  if (!loader) return null;
  const cached = lazyCache.get(kind);
  if (cached) return cached;
  const component = lazy(loader);
  lazyCache.set(kind, component);
  return component;
}

export interface LazyEmbedProps extends EmbedProps {
  kind: string;
}

/** Code-split embed renderer with a lightweight Suspense fallback. */
export function LazyEmbed({ kind, ...props }: LazyEmbedProps) {
  const Embed = getLazyEmbed(kind);
  if (!Embed) {
    return (
      <div className="px-3 py-2 text-xs italic text-slate-500">
        Unsupported embed: {kind}
      </div>
    );
  }
  return (
    <Suspense
      fallback={
        <div className="px-3 py-2 text-xs text-slate-500">Loading embed…</div>
      }
    >
      <Embed {...props} />
    </Suspense>
  );
}

/** Whether the embed registry has a code-split chunk for this catalogue kind. */
export function isKnownEmbedKind(kind: string): boolean {
  return kind in EMBED_LOADERS;
}
